import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { EllipsisIcon } from 'lucide-react'
import { ReactNode } from 'react'
import { Z_INDEX_POPUP } from '../../constants'
import { Tooltip } from '../../tooltip'

export type WidgetActionMenuItem = {
	id: string
	label: ReactNode
	onSelect: () => void
	icon?: ReactNode
	variant?: 'default' | 'danger'
	disabled?: boolean
	/** Shown on hover — typically explains why an item is disabled. */
	tooltip?: string
}

export interface WidgetActionMenuProps {
	items: ReadonlyArray<WidgetActionMenuItem>
	disabled?: boolean
	ariaLabel?: string
}

export function WidgetActionMenu({ items, disabled = false, ariaLabel = 'Open menu' }: WidgetActionMenuProps) {
	return (
		<Popover className="grid place-items-center">
			{({ close }) => (
				<>
					<PopoverButton
						className={ctl(`
							flex items-center justify-center size-8 rounded-md
							text-grey-500 bg-brand-50 hover:bg-brand-200 data-open:bg-brand-200
							outline-0 hover:outline-brand-500
							transition-colors
							${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
						`)}
						disabled={disabled}
						aria-label={ariaLabel}
					>
						<EllipsisIcon className="size-4" />
					</PopoverButton>

					<PopoverPanel
						anchor="bottom end"
						style={{ zIndex: Z_INDEX_POPUP }}
						className="[--anchor-gap:6px] min-w-40 bg-white ring-1 ring-grey-100 shadow-lg rounded-lg p-1"
					>
						<ul className="flex flex-col">
							{items.map((item) => (
								<li key={item.id}>
									<ActionMenuItemButton
										item={item}
										onSelected={() => {
											item.onSelect()
											close()
										}}
									/>
								</li>
							))}
						</ul>
					</PopoverPanel>
				</>
			)}
		</Popover>
	)
}

function ActionMenuItemButton({ item, onSelected }: { item: WidgetActionMenuItem; onSelected: () => void }) {
	const isDanger = item.variant === 'danger'

	const button = (
		<button
			type="button"
			data-prevent-focus-loss
			disabled={item.disabled}
			onClick={onSelected}
			className={ctl(`
				w-full flex items-center gap-2 text-left text-sm rounded-md px-2.5 py-1.5
				transition-colors
				${
				isDanger
					? 'text-red-600 hover:bg-red-50'
					: 'text-grey-700 hover:bg-grey-100 hover:text-grey-900'
			}
				disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent
				${item.disabled ? '' : 'cursor-pointer'}
			`)}
		>
			{item.icon && <span className="shrink-0 grid place-items-center size-4">{item.icon}</span>}
			<span className="truncate">{item.label}</span>
		</button>
	)

	if (!item.tooltip) {
		return button
	}

	return (
		<Tooltip content={item.tooltip} wrapperClassName="block">
			{button}
		</Tooltip>
	)
}
